import { ANALYTICS_EVENTS } from "../analytics-store.tsx";
import { APPS } from "../constants/AppsConstants.ts";
import { getSelectedOrganization } from "../helpers/localStorage.ts";
import { PlaygroundImage } from "../models/playground.ts";
import { modelTypes, TextToImageConfigType } from "../models/text-to-image.ts";
import { isAutomotiveApp, isFoxApps } from "../utils/index.ts";
import { useImageToImageConfig } from "./useImageToImageConfig.tsx";
import { useAppStore } from "./useStores.tsx";

export const useLogEvents = () => {
	const {
		canExpandBackground,
		canRemoveBackground,
		canBlurBackground,
		canApplySolidBackground,
		canGenerateBackgroundByText,
		canApplyAspectRatio,
		canAppleCustomSize,
		canAppleMediumDestination,
		canGenerateForegroundPlacement,
	} = useImageToImageConfig();

	const { analyticsStore, textToImageStore, playgroundStore } = useAppStore();
	const { logEvent } = analyticsStore;

	const imageEditingParams = {
		orgName: getSelectedOrganization()?.organization.name,
		viewMode: playgroundStore.imageEditingViewMode,
	};

	const logImageDownloadEvents = (images: PlaygroundImage[], fileType: string, resolution?: number) => {
		const { logEvent } = analyticsStore;
		images.forEach((image) => {
			const config = image.config as TextToImageConfigType;
			const additionalParams: { fileType: string; app?: string; model_name?: string } = {
				fileType: fileType,
				app: image.type,
			};
			if (image.type === APPS.TEXT_TO_IMAGE || image.type === APPS.TEXT_TO_VECTOR) {
				additionalParams.model_name = config.model_name;

				logEvent(ANALYTICS_EVENTS.IMAGE_GENERATION_DOWNLOAD_GENERAL, additionalParams);

				if (config.tailored_model_id) {
					logEvent(ANALYTICS_EVENTS.IMAGE_GENERATION_DOWNLOAD_TAILORED_MODELS, {
						tailoredModelId: config.tailored_model_id,
						...additionalParams,
					});
				} else {
					const modelEventMap = {
						[modelTypes.Fast]: ANALYTICS_EVENTS.IMAGE_GENERATION_DOWNLOAD_BRIA_FAST,
						[modelTypes.Base]: ANALYTICS_EVENTS.IMAGE_GENERATION_DOWNLOAD_BRIA_BASE,
						[modelTypes.Hd]: ANALYTICS_EVENTS.IMAGE_GENERATION_DOWNLOAD_BRIA_HD,
					};

					const modelEvent = modelEventMap[config.model as keyof typeof modelEventMap];
					if (modelEvent) {
						logEvent(modelEvent, additionalParams);
					}

					if (textToImageStore.isImageReferenceEnabled()) {
						logEvent(ANALYTICS_EVENTS.IMAGE_GENERATION_DOWNLOAD_IMAGE_REFERENCE, additionalParams);
					}

					if (resolution && resolution > 1) {
						logEvent(ANALYTICS_EVENTS.IMAGE_GENERATION_INCREASE_RESOLUTION, additionalParams);
					}
				}
			} else if (image.type === APPS.IMAGE_TO_IMAGE) {
				if (isFoxApps()) {
					analyticsStore.logImageToImageEvent(ANALYTICS_EVENTS.IMAGE_DOWNLOAD_ITI, image);
				} else {
					// Background configs events
					canExpandBackground?.() &&
						logEvent(ANALYTICS_EVENTS.IMAGE_EDITING_DOWNLOAD_EXPAND_IMAGE, additionalParams);
					canRemoveBackground?.() &&
						logEvent(ANALYTICS_EVENTS.IMAGE_EDITING_DOWNLOAD_REMOVE_BACKGROUND, additionalParams);
					canBlurBackground?.() &&
						logEvent(ANALYTICS_EVENTS.IMAGE_EDITING_DOWNLOAD_BLUR_BACKGROUND, additionalParams);
					canApplySolidBackground?.() &&
						logEvent(ANALYTICS_EVENTS.IMAGE_EDITING_DOWNLOAD_SOLID_BACKGROUND, additionalParams);
					canGenerateBackgroundByText?.() &&
						logEvent(ANALYTICS_EVENTS.IMAGE_EDITING_DOWNLOAD_GENERATE_BACKGROUND_BY_TEXT, additionalParams);

					// Size configs events
					canApplyAspectRatio?.() &&
						logEvent(ANALYTICS_EVENTS.IMAGE_EDITING_DOWNLOAD_ASPECT_RATIO, additionalParams);
					canAppleCustomSize?.() &&
						logEvent(ANALYTICS_EVENTS.IMAGE_EDITING_DOWNLOAD_CUSTOM_SIZE, additionalParams);
					canAppleMediumDestination?.() &&
						logEvent(ANALYTICS_EVENTS.IMAGE_EDITING_DOWNLOAD_MEDIUM_DESTINATION, additionalParams);

					// Foreground placement configs events
					canGenerateForegroundPlacement?.() &&
						logEvent(ANALYTICS_EVENTS.IMAGE_EDITING_DOWNLOAD_FOREGROUND, additionalParams);

					if (resolution && resolution > 1) {
						logEvent(ANALYTICS_EVENTS.IMAGE_EDITING_INCREASE_RESOLUTION, additionalParams);
					}
				}
			} else if (image.type === APPS.PRODUCT_PLACEMENT) {
				analyticsStore.logEvent(ANALYTICS_EVENTS.IMAGE_DOWNLOAD_PP, additionalParams);
			} else {
				analyticsStore.logEvent(ANALYTICS_EVENTS.IMAGE_DOWNLOAD, additionalParams);
			}
		});
	};

	const logImageEditingEvents = async () => {
		if (!isFoxApps()) {
			logEvent(ANALYTICS_EVENTS.IMAGE_EDITING_GENERATE, imageEditingParams);

			// Background configs events
			canExpandBackground() && logEvent(ANALYTICS_EVENTS.IMAGE_EDITING_EXPAND_IMAGE, imageEditingParams);
			canRemoveBackground() && logEvent(ANALYTICS_EVENTS.IMAGE_EDITING_REMOVE_BACKGROUND, imageEditingParams);
			canBlurBackground() && logEvent(ANALYTICS_EVENTS.IMAGE_EDITING_BLUR_BACKGROUND, imageEditingParams);
			canApplySolidBackground() && logEvent(ANALYTICS_EVENTS.IMAGE_EDITING_SOLID_BACKGROUND, imageEditingParams);
			canGenerateBackgroundByText() &&
				logEvent(ANALYTICS_EVENTS.IMAGE_EDITING_GENERATE_BACKGROUND_BY_TEXT, imageEditingParams);

			// Size configs events
			canApplyAspectRatio() && logEvent(ANALYTICS_EVENTS.IMAGE_EDITING_ASPECT_RATIO, imageEditingParams);
			canAppleCustomSize() && logEvent(ANALYTICS_EVENTS.IMAGE_EDITING_CUSTOM_SIZE, imageEditingParams);
			canAppleMediumDestination() &&
				logEvent(ANALYTICS_EVENTS.IMAGE_EDITING_MEDIUM_DESTINATION, imageEditingParams);

			// Foreground placement configs
			canGenerateForegroundPlacement() &&
				logEvent(ANALYTICS_EVENTS.IMAGE_EDITING_GENERATE_FOREGROUND, imageEditingParams);
		}
	};

	const logEraseObjectEvent = () => {
		const eventName = isAutomotiveApp() ? ANALYTICS_EVENTS.AUTOMOTIVE_ERASE : ANALYTICS_EVENTS.IMAGE_EDITING_ERASE;
		logEvent(eventName, imageEditingParams);
	};

	const logGenerativeFillEvent = () => {
		const eventName = isAutomotiveApp()
			? ANALYTICS_EVENTS.AUTOMOTIVE_GENERATIVE_FILL
			: ANALYTICS_EVENTS.IMAGE_EDITING_GENERATIVE_FILL;
		logEvent(eventName, imageEditingParams);
	};

	const logExpandEvent = () => {
		const eventName = isAutomotiveApp()
			? ANALYTICS_EVENTS.AUTOMOTIVE_EXPAND_IMAGE
			: ANALYTICS_EVENTS.IMAGE_EDITING_EXPAND_IMAGE;
		logEvent(eventName, imageEditingParams);
	};
	const logCropEvent = () => {
		const eventName = isAutomotiveApp()
			? ANALYTICS_EVENTS.AUTOMOTIVE_CROP
			: ANALYTICS_EVENTS.IMAGE_EDITING_CROP_IMAGE;
		logEvent(eventName, imageEditingParams);
	};

	const logIncreaseResolutionEvent = () => {
		const eventName = isAutomotiveApp()
			? ANALYTICS_EVENTS.AUTOMOTIVE_INCREASE_RESOLUTION
			: ANALYTICS_EVENTS.IMAGE_EDITING_INCREASE_RESOLUTION;
		logEvent(eventName, imageEditingParams);
	};

	const logAutomotiveEvents = () => {
		logEvent(ANALYTICS_EVENTS.AUTOMOTIVE_GENERATE, imageEditingParams);

		// Background configs events
		canExpandBackground() && logEvent(ANALYTICS_EVENTS.AUTOMOTIVE_EXPAND_IMAGE, imageEditingParams);
		canRemoveBackground() && logEvent(ANALYTICS_EVENTS.AUTOMOTIVE_REMOVE_BACKGROUND, imageEditingParams);
		canBlurBackground() && logEvent(ANALYTICS_EVENTS.AUTOMOTIVE_BLUR_BACKGROUND, imageEditingParams);
		canApplySolidBackground() && logEvent(ANALYTICS_EVENTS.AUTOMOTIVE_SOLID_BACKGROUND, imageEditingParams);
		canGenerateBackgroundByText() &&
			logEvent(ANALYTICS_EVENTS.AUTOMOTIVE_GENERATE_BACKGROUND_BY_TEXT, imageEditingParams);

		// Size configs events
		canApplyAspectRatio() && logEvent(ANALYTICS_EVENTS.AUTOMOTIVE_ASPECT_RATIO, imageEditingParams);
		canAppleCustomSize() && logEvent(ANALYTICS_EVENTS.AUTOMOTIVE_CUSTOM_SIZE, imageEditingParams);
		canAppleMediumDestination() && logEvent(ANALYTICS_EVENTS.AUTOMOTIVE_MEDIUM_DESTINATION, imageEditingParams);

		// Foreground placement configs
		canGenerateForegroundPlacement() &&
			logEvent(ANALYTICS_EVENTS.AUTOMOTIVE_GENERATE_FOREGROUND, imageEditingParams);
	};

	const logTiresRefinementEvent = () => {
		logEvent(ANALYTICS_EVENTS.AUTOMOTIVE_TIRES_REFINEMENT, imageEditingParams);
	};

	const logReflectionsEvent = () => {
		logEvent(ANALYTICS_EVENTS.AUTOMOTIVE_REFLECTIONS, imageEditingParams);
	};

	const logEffectsEvent = () => {
		logEvent(ANALYTICS_EVENTS.AUTOMOTIVE_EFFECTS, imageEditingParams);
	};

	const logHarmonizationEvent = () => {
		logEvent(ANALYTICS_EVENTS.AUTOMOTIVE_HARMONIZATION, imageEditingParams);
	};

	return {
		logImageDownloadEvents,
		logImageEditingEvents,
		logEraseObjectEvent,
		logGenerativeFillEvent,
		logExpandEvent,
		logCropEvent,
		logIncreaseResolutionEvent,
		logAutomotiveEvents,
		logTiresRefinementEvent,
		logReflectionsEvent,
		logEffectsEvent,
		logHarmonizationEvent,
	};
};
